import * as React from 'react'
import { useEffect } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  ExpandedState,
  getExpandedRowModel,
  TableMeta,
} from '@tanstack/react-table'
import { logger } from '@magnetar/utils'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import TodayAdPlayCount from '@/components/TodayAdPlayCount'
import { Clinic, BroadcastDevice } from '@magnetar/admin-api-interfaces'
import { useBroadcastDeviceDialog } from '@/stores/useBroadcastDeviceDialog'
import { useInvestorDeviceStats } from '@/hooks/useInvestorDeviceStats'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onSearch?: (value: string) => void
  meta?: {
    reloadData: () => void
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onSearch,
  meta,
}: DataTableProps<TData, TValue>) {
  const [searchValue, setSearchValue] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'clinic_id', desc: false }])
  const [expanded, setExpanded] = React.useState<ExpandedState>(true)
  const { openDialog } = useBroadcastDeviceDialog()

  // 获取投资者设备状态数据
  const { stats } = useInvestorDeviceStats()

  // 检查设备是否在线
  const isDeviceOnline = (clinicId: number, broadcastDeviceId: number): boolean => {
    if (!stats?.clinicStats) return false

    const clinicStats = stats.clinicStats.find((clinic) => clinic.clinicId === clinicId)
    if (!clinicStats) return false

    // 在 onlineDeviceIds 中查找对应的设备ID，如果找到就表示真正在线
    return clinicStats.onlineDeviceIds.includes(broadcastDeviceId)
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      expanded,
    },
    meta: meta as TableMeta<TData>,
  })

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch?.(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue, onSearch])

  return (
    <div>
      <div className="flex items-center py-4">
        <Input
          placeholder="搜尋診所名稱..."
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {/* 添加空的表頭單元格，對應展開按鈕列 */}
                <TableHead className="w-10"></TableHead>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={(header.column.columnDef.meta as { className?: string })?.className}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                // 獲取原始資料
                const clinic = row.original as Clinic
                const hasDevices = clinic.broadcast_devices && clinic.broadcast_devices.length > 0

                return (
                  <React.Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && 'selected'}>
                      {/* 添加展開按鈕 */}
                      <TableCell className="w-10">
                        {hasDevices && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              row.toggleExpanded()
                            }}
                          >
                            {row.getIsExpanded() ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>

                      {/* 原始資料欄位 */}
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={(cell.column.columnDef.meta as { className?: string })?.className}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* 展開的子表格 - 廣播裝置列表 */}
                    {row.getIsExpanded() && hasDevices && (
                      <TableRow>
                        <TableCell colSpan={columns.length + 1} className="p-0">
                          <div className="bg-muted/50 p-4">
                            <h3 className="text-sm font-medium mb-2">廣播裝置列表</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {clinic.broadcast_devices.map((device: BroadcastDevice) => (
                                <div
                                  key={device.broadcast_device_id}
                                  className="bg-card border rounded-md p-3 hover:border-primary cursor-pointer transition-colors"
                                  onClick={() => {
                                    logger.log(device)
                                    openDialog(device)
                                  }}
                                >
                                  {/* 連線狀態與播放次數區塊 */}
                                  <div className="flex gap-2 mb-2">
                                    <div className="flex-1 flex flex-col items-center justify-center border rounded px-2 py-1">
                                      <div className="flex items-center gap-1 mb-1">
                                        <span
                                          className={`inline-block w-2 h-2 rounded-full ${
                                            isDeviceOnline(
                                              clinic.clinic_id,
                                              device.broadcast_device_id,
                                            )
                                              ? 'bg-green-500'
                                              : 'bg-red-500'
                                          }`}
                                          aria-label={
                                            isDeviceOnline(
                                              clinic.clinic_id,
                                              device.broadcast_device_id,
                                            )
                                              ? '已連線'
                                              : '未連線'
                                          }
                                        />
                                        <span className="text-xs text-muted-foreground">
                                          連線狀態
                                        </span>
                                      </div>
                                      <span className="text-sm font-medium">
                                        {isDeviceOnline(
                                          clinic.clinic_id,
                                          device.broadcast_device_id,
                                        )
                                          ? '已連線'
                                          : '未連線'}
                                      </span>
                                    </div>
                                    <TodayAdPlayCount
                                      broadcastDeviceId={device.broadcast_device_id}
                                    />
                                  </div>
                                  {/* 原本內容 */}
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="font-medium">{device.name}</span>
                                    <Badge
                                      variant={device.is_active ? 'default' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {device.is_active ? '啟用中' : '停用'}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    <div className="flex justify-between mb-1">
                                      <span>ID:</span>
                                      <span>{device.broadcast_device_id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>類型:</span>
                                      <span>{device.broadcast_type}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
